import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Select,
    Loader,
    Center,
    Table,
    Badge,
    Button,
    SimpleGrid,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billlede-1.png";

import type {
    TransportDepartureDto,
    AdminTransportRouteBookingDto,
    AdminTransportRouteBookingSummaryDto,
    AdminTransportRouteBookingStopSummaryDto,
    AdminTransportRouteDepartureCapacityDto,
} from "./types";

const apiUrl = process.env.REACT_APP_API_URL;

type TransportAdminEventDto = {
    eventId: number;
    name: string;
};

export default function TransportRouteBookingsAdminPage() {
    const [events, setEvents] = useState<TransportAdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [departures, setDepartures] = useState<TransportDepartureDto[]>([]);
    const [selectedDepartureId, setSelectedDepartureId] = useState<number | null>(null);

    const [summary, setSummary] = useState<AdminTransportRouteBookingSummaryDto | null>(null);
    const [stopSummary, setStopSummary] = useState<AdminTransportRouteBookingStopSummaryDto[]>([]);
    const [bookings, setBookings] = useState<AdminTransportRouteBookingDto[]>([]);

    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState("");

    const [capacityRows, setCapacityRows] = useState<AdminTransportRouteDepartureCapacityDto[]>([]);
    const [capacityFilter, setCapacityFilter] = useState<string>("all");
    const [routeFilter, setRouteFilter] = useState<string>("all");
    const [dateFilter, setDateFilter] = useState<string>("all");

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${apiUrl}/api/transport/admin/events`);
                setEvents(res.data ?? []);

                if (res.data?.length) {
                    setEventId(res.data[0].eventId);
                }
            } catch (err) {
                console.error(err);
                setError("Could not load events.");
            }
        })();
    }, []);

    const eventOptions = useMemo(
        () => events.map((e) => ({ value: String(e.eventId), label: e.name })),
        [events]
    );

    const departureOptions = useMemo(
        () =>
            departures.map((d) => ({
                value: String(d.transportDepartureID),
                label: `${d.routeName} • ${d.departureDate} • ${d.startTime}`,
            })),
        [departures]
    );

    const routeOptions = useMemo(() => {
        const uniqueRoutes = Array.from(new Set(capacityRows.map((x) => x.routeName)));
        return [
            { value: "all", label: "All routes" },
            ...uniqueRoutes.map((route) => ({
                value: route,
                label: route,
            })),
        ];
    }, [capacityRows]);

    const dateOptions = useMemo(() => {
        return Array.from(new Set(capacityRows.map((x) => x.departureDate))).sort();
    }, [capacityRows]);

    const loadDepartures = async (eid: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/departures`, {
            params: { eventId: eid },
        });

        const data = res.data ?? [];
        setDepartures(data);

        if (data.length > 0) {
            setSelectedDepartureId((prev) => {
                if (prev && data.some((x: TransportDepartureDto) => x.transportDepartureID === prev)) {
                    return prev;
                }
                return data[0].transportDepartureID;
            });
        } else {
            setSelectedDepartureId(null);
        }
    };

    const loadBookings = async (eid: number, departureId: number | null) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/bookings`, {
            params: {
                eventId: eid,
                departureId: departureId ?? undefined,
            },
        });

        setBookings(res.data ?? []);
    };

    const loadSummary = async (departureId: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/bookings/summary`, {
            params: { departureId },
        });

        setSummary(res.data);
    };

    const loadStopSummary = async (departureId: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/bookings/stops`, {
            params: { departureId },
        });

        setStopSummary(res.data ?? []);
    };

    const loadCapacityOverview = async (eid: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/departures/capacity-overview`, {
            params: { eventId: eid },
        });

        setCapacityRows(res.data ?? []);
    };

    useEffect(() => {
        if (!eventId) return;

        const run = async () => {
            try {
                setLoading(true);
                setError("");

                await loadDepartures(eventId);
                await loadCapacityOverview(eventId);
            } catch (err) {
                console.error(err);
                setError("Could not load departures.");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [eventId]);

    useEffect(() => {
        if (!eventId) return;

        const run = async () => {
            try {
                setLoadingDetails(true);
                setError("");

                await loadBookings(eventId, selectedDepartureId);

                if (selectedDepartureId) {
                    await loadSummary(selectedDepartureId);
                    await loadStopSummary(selectedDepartureId);
                } else {
                    setSummary(null);
                    setStopSummary([]);
                }
            } catch (err) {
                console.error(err);
                setError("Could not load booking overview.");
            } finally {
                setLoadingDetails(false);
            }
        };

        run();
    }, [eventId, selectedDepartureId]);

    const filteredCapacityRows = useMemo(() => {
        return capacityRows.filter((x) => {
            const matchesStatus =
                capacityFilter === "all" || x.statusLabel === capacityFilter;

            const matchesRoute =
                routeFilter === "all" || x.routeName === routeFilter;

            const matchesDate =
                dateFilter === "all" || x.departureDate === dateFilter;

            return matchesStatus && matchesRoute && matchesDate;
        });
    }, [capacityRows, capacityFilter, routeFilter, dateFilter]);

    const cancelBooking = async (id: number) => {
        try {
            await axios.post(`${apiUrl}/api/transport-route/admin/bookings/${id}/cancel`);

            showNotification({
                title: "Success",
                message: "Booking cancelled.",
            });

            if (eventId) {
                await loadBookings(eventId, selectedDepartureId);
                await loadCapacityOverview(eventId);
            }

            if (selectedDepartureId) {
                await loadSummary(selectedDepartureId);
                await loadStopSummary(selectedDepartureId);
            }
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Error",
                message: "Could not cancel booking.",
                color: "red",
            });
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Transport Route Bookings"
                titleDa="Transport rute-bookinger"
                descriptionEn="Overview of bookings on route departures"
                descriptionDa="Overblik over bookinger på ruteafgange"
                imageSrc={Billede1}
            />

            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Transport Route Bookings</Title>
                            <Text c="dimmed" size="sm">
                                View bookings, stop overview, and load by departure.
                            </Text>
                        </Stack>

                        <Group align="flex-end">
                            <Select
                                label="Select event"
                                data={eventOptions}
                                value={eventId ? String(eventId) : null}
                                onChange={(v) => {
                                    setSummary(null);
                                    setStopSummary([]);
                                    setBookings([]);
                                    setSelectedDepartureId(null);
                                    setEventId(v ? Number(v) : null);
                                    setRouteFilter("all");
                                    setDateFilter("all");
                                    setCapacityFilter("all");
                                }}
                                searchable
                                w={280}
                            />

                            <Select
                                label="Select departure"
                                data={departureOptions}
                                value={selectedDepartureId ? String(selectedDepartureId) : null}
                                onChange={(v) => setSelectedDepartureId(v ? Number(v) : null)}
                                searchable
                                w={380}
                                disabled={!eventId || departures.length === 0}
                                placeholder="All / select departure"
                                clearable
                            />
                        </Group>
                    </Group>
                </Paper>

                {!!error && (
                    <Paper withBorder p="md" radius="md" mt="md">
                        <Text c="red">{error}</Text>
                    </Paper>
                )}

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Group justify="space-between" align="flex-end" mb="md" wrap="wrap">
                        <Text fw={700}>Capacity overview</Text>

                        <Group align="flex-end" wrap="wrap">
                            <Select
                                label="Route"
                                value={routeFilter}
                                onChange={(v) => setRouteFilter(v ?? "all")}
                                data={routeOptions}
                                w={220}
                            />

                            <Select
                                label="Status"
                                value={capacityFilter}
                                onChange={(v) => setCapacityFilter(v ?? "all")}
                                data={[
                                    { value: "all", label: "All" },
                                    { value: "OK", label: "OK" },
                                    { value: "Snart fuld", label: "Nearly full" },
                                    { value: "Overbooked", label: "Overbooked" },
                                ]}
                                w={220}
                            />
                        </Group>
                    </Group>

                    <Group mb="md" wrap="wrap">
                        <Badge
                            variant={dateFilter === "all" ? "filled" : "light"}
                            style={{ cursor: "pointer" }}
                            onClick={() => setDateFilter("all")}
                        >
                            All dates
                        </Badge>

                        {dateOptions.map((date) => {
                            const count = capacityRows.filter((x) => x.departureDate === date).length;

                            return (
                                <Badge
                                    key={date}
                                    variant={dateFilter === date ? "filled" : "light"}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setDateFilter(date)}
                                >
                                    {date} ({count})
                                </Badge>
                            );
                        })}
                    </Group>

                    {loading ? (
                        <Center py="lg">
                            <Loader size="sm" />
                        </Center>
                    ) : (
                        <Table.ScrollContainer minWidth={1100}>
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Route</Table.Th>
                                        <Table.Th>Date</Table.Th>
                                        <Table.Th>Time</Table.Th>
                                        <Table.Th>Capacity</Table.Th>
                                        <Table.Th>Booked</Table.Th>
                                        <Table.Th>Available</Table.Th>
                                        <Table.Th>Overbooked</Table.Th>
                                        <Table.Th>Load</Table.Th>
                                        <Table.Th>Status</Table.Th>
                                        <Table.Th></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {filteredCapacityRows.map((row) => (
                                        <Table.Tr key={row.transportDepartureID}>
                                            <Table.Td>{row.routeName}</Table.Td>
                                            <Table.Td>{row.departureDate}</Table.Td>
                                            <Table.Td>{row.departureTime}</Table.Td>
                                            <Table.Td>{row.capacity}</Table.Td>
                                            <Table.Td>{row.bookedPassengers}</Table.Td>
                                            <Table.Td>{row.remainingSeats}</Table.Td>
                                            <Table.Td>{row.overbookedBy}</Table.Td>
                                            <Table.Td>{row.loadPercent}%</Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={
                                                        row.statusLabel === "Overbooked"
                                                            ? "red"
                                                            : row.statusLabel === "Snart fuld"
                                                                ? "yellow"
                                                                : "green"
                                                    }
                                                    variant="light"
                                                >
                                                    {row.statusLabel === "Snart fuld"
                                                        ? "Nearly full"
                                                        : row.statusLabel}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    onClick={() => setSelectedDepartureId(row.transportDepartureID)}
                                                >
                                                    View
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}

                                    {filteredCapacityRows.length === 0 && (
                                        <Table.Tr>
                                            <Table.Td colSpan={10}>
                                                <Text c="dimmed" ta="center" py="md">
                                                    No departures match the selected filters.
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Paper>

                {loading ? (
                    <Center py="xl">
                        <Loader />
                    </Center>
                ) : (
                    <>
                        {selectedDepartureId && summary && (
                            <Paper withBorder p="lg" radius="md" mt="md">
                                <Stack gap="sm">
                                    <Text fw={700}>
                                        {summary.routeName} • {summary.departureDate} • {summary.departureTime}
                                    </Text>

                                    <SimpleGrid cols={{ base: 2, md: 4 }}>
                                        <Paper withBorder p="md" radius="md">
                                            <Text size="sm" c="dimmed">Capacity</Text>
                                            <Text fw={700} size="lg">{summary.capacity}</Text>
                                        </Paper>

                                        <Paper withBorder p="md" radius="md">
                                            <Text size="sm" c="dimmed">Booked</Text>
                                            <Text fw={700} size="lg">{summary.bookedPassengers}</Text>
                                        </Paper>

                                        <Paper withBorder p="md" radius="md">
                                            <Text size="sm" c="dimmed">Available</Text>
                                            <Text fw={700} size="lg">{summary.remainingSeats}</Text>
                                        </Paper>

                                        <Paper withBorder p="md" radius="md">
                                            <Text size="sm" c="dimmed">Overbooked</Text>
                                            <Text fw={700} size="lg">
                                                {summary.overbookedBy > 0 ? summary.overbookedBy : 0}
                                            </Text>
                                        </Paper>
                                    </SimpleGrid>
                                </Stack>
                            </Paper>
                        )}

                        {selectedDepartureId && (
                            <Paper withBorder p="lg" radius="md" mt="md">
                                <Text fw={700} mb="sm">
                                    Stop overview
                                </Text>

                                {loadingDetails ? (
                                    <Center py="lg">
                                        <Loader size="sm" />
                                    </Center>
                                ) : (
                                    <Table.ScrollContainer minWidth={900}>
                                        <Table striped highlightOnHover>
                                            <Table.Thead>
                                                <Table.Tr>
                                                    <Table.Th>Stop</Table.Th>
                                                    <Table.Th>Time</Table.Th>
                                                    <Table.Th>Boards here</Table.Th>
                                                    <Table.Th>Gets off here</Table.Th>
                                                    <Table.Th>Onboard after stop</Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {stopSummary.map((s) => (
                                                    <Table.Tr key={s.transportDepartureStopID}>
                                                        <Table.Td>{s.stopName}</Table.Td>
                                                        <Table.Td>{s.pickupTime}</Table.Td>
                                                        <Table.Td>{s.boardedHere}</Table.Td>
                                                        <Table.Td>{s.gotOffHere ?? 0}</Table.Td>
                                                        <Table.Td>{s.onboardAfterStop}</Table.Td>
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    </Table.ScrollContainer>
                                )}
                            </Paper>
                        )}

                        <Paper withBorder p="lg" radius="md" mt="md">
                            <Text fw={700} mb="sm">
                                Bookings
                            </Text>

                            {loadingDetails ? (
                                <Center py="lg">
                                    <Loader size="sm" />
                                </Center>
                            ) : (
                                <Table.ScrollContainer minWidth={1450}>
                                    <Table striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Name</Table.Th>
                                                <Table.Th>Email</Table.Th>
                                                <Table.Th>WhatsApp</Table.Th>
                                                <Table.Th>Route</Table.Th>
                                                <Table.Th>Date</Table.Th>
                                                <Table.Th>Departure</Table.Th>
                                                <Table.Th>Pickup stop</Table.Th>
                                                <Table.Th>Pickup time</Table.Th>
                                                <Table.Th>Dropoff stop</Table.Th>
                                                <Table.Th>Passengers</Table.Th>
                                                <Table.Th>Status</Table.Th>
                                                <Table.Th>Created</Table.Th>
                                                <Table.Th></Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {bookings.map((b) => (
                                                <Table.Tr key={b.transportRouteBookingID}>
                                                    <Table.Td>{b.contactName ?? "-"}</Table.Td>
                                                    <Table.Td>{b.contactEmail ?? "-"}</Table.Td>
                                                    <Table.Td>
                                                        {b.whatsappCountryCode && b.whatsappNumber
                                                            ? `${b.whatsappCountryCode}-${b.whatsappNumber}`
                                                            : "-"}
                                                    </Table.Td>
                                                    <Table.Td>{b.routeName}</Table.Td>
                                                    <Table.Td>{b.departureDate}</Table.Td>
                                                    <Table.Td>{b.departureTime}</Table.Td>
                                                    <Table.Td>{b.pickupStopName}</Table.Td>
                                                    <Table.Td>{b.pickupTime}</Table.Td>
                                                    <Table.Td>{b.dropoffStopName ?? "-"}</Table.Td>
                                                    <Table.Td>{b.passengerCount}</Table.Td>
                                                    <Table.Td>
                                                        <Badge
                                                            color={b.status === "confirmed" ? "green" : "gray"}
                                                            variant="light"
                                                        >
                                                            {b.status}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {new Date(b.createdAtUtc).toLocaleString()}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {b.status === "confirmed" && (
                                                            <Button
                                                                size="xs"
                                                                color="red"
                                                                variant="light"
                                                                onClick={() =>
                                                                    cancelBooking(b.transportRouteBookingID)
                                                                }
                                                            >
                                                                Cancel
                                                            </Button>
                                                        )}
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                            )}
                        </Paper>
                    </>
                )}
            </Container>
        </div>
    );
}